/**
 * Eskalasi WhatsApp — K207 / W5 (SESI-58 U2).
 *
 * AI Cekat menyerahkan percakapan ke CS manusia (komplain atau tidak bisa menjawab) lewat alat `eskalasi`.
 * Balasan ke customer tetap ditulis di Cekat, di percakapan yang sama (K123 butir 2); panel ini tempat CS /
 * Kepala Cabang region (dan Owner) menandai "Saya ambil alih" lalu "Selesai" — bahan laporan frekuensi ambil
 * alih (W6). Menandai memadamkan notifikasinya, jadi tangga eskalasi K116 berhenti.
 * Sumber: GET /api/wa-kanal/eskalasi, POST /api/wa-kanal/eskalasi/{id}/ambil-alih | /selesai.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { formatDateTime } from "@/lib/format";
import { mintaTeks } from "@/lib/dialogAplikasi";
import { toast } from "sonner";

const LABEL_STATUS = { TERBUKA: "Belum ditangani", DIAMBIL_ALIH: "Sedang ditangani", SELESAI: "Selesai" };

export default function PanelEskalasiWa() {
  const [baris, setBaris] = useState([]);
  const [muat, setMuat] = useState(true);
  const [sibuk, setSibuk] = useState(null);
  const [lihatSelesai, setLihatSelesai] = useState(false);

  const muatUlang = useCallback(async () => {
    setMuat(true);
    try {
      const { data } = await api.get("/wa-kanal/eskalasi", { params: { status: lihatSelesai ? "SELESAI" : "TERBUKA_DIAMBIL" } });
      setBaris(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Daftar eskalasi WhatsApp gagal dimuat."));
    } finally {
      setMuat(false);
    }
  }, [lihatSelesai]);

  useEffect(() => { muatUlang(); }, [muatUlang]);

  async function ambilAlih(e) {
    setSibuk(e.id);
    try {
      await api.post(`/wa-kanal/eskalasi/${e.id}/ambil-alih`);
      toast.success("Ditandai sedang Anda tangani. Balas customer di Cekat, di percakapan yang sama.");
      muatUlang();
    } catch (err) { toast.error(apiErrorMessage(err, "Eskalasi belum bisa ditandai.")); }
    finally { setSibuk(null); }
  }

  async function selesai(e) {
    const catatan = await mintaTeks("Catatan penyelesaian (boleh dikosongkan):", { judul: "Tandai selesai", ya: "Tandai selesai" });
    if (catatan === null) return;
    setSibuk(e.id);
    try {
      await api.post(`/wa-kanal/eskalasi/${e.id}/selesai`, { catatan: catatan || null });
      toast.success("Eskalasi ditandai selesai.");
      muatUlang();
    } catch (err) { toast.error(apiErrorMessage(err, "Eskalasi belum bisa ditandai selesai.")); }
    finally { setSibuk(null); }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="panel-eskalasi-wa">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-foreground">Customer WhatsApp yang perlu dibantu CS</h2>
          <p className="text-xs text-muted-foreground">
            Diteruskan AI karena komplain atau pertanyaan yang tidak bisa dijawab. Balas di Cekat, lalu tandai di sini.
          </p>
        </div>
        <button type="button" className="text-xs underline text-muted-foreground"
                onClick={() => setLihatSelesai((v) => !v)} data-testid="eskalasi-ganti-saringan">
          {lihatSelesai ? "Tampilkan yang belum selesai" : "Tampilkan yang sudah selesai"}
        </button>
      </div>
      {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}
      {!muat && baris.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {lihatSelesai ? "Belum ada eskalasi yang selesai." : "Tidak ada customer yang menunggu bantuan CS."}
        </p>
      )}
      <div className="space-y-2">
        {baris.map((e) => (
          <div key={e.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`eskalasi-${e.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{e.kategori_label}</span>
              <span>{e.contact_phone}</span>
              {e.nomor_pesanan && <span>· {e.nomor_pesanan}</span>}
              <span>· {formatDateTime(e.created_at)}</span>
              <span className="rounded border border-border px-1.5">{LABEL_STATUS[e.status] || e.status}</span>
              {e.diambil_oleh_nama && <span>· ditangani {e.diambil_oleh_nama}</span>}
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words text-foreground">{e.ringkasan}</p>
            {e.catatan_selesai && <p className="mt-1 text-xs text-muted-foreground">Catatan: {e.catatan_selesai}</p>}
            {e.status !== "SELESAI" && (
              <div className="mt-2 flex gap-2">
                {e.status === "TERBUKA" && (
                  <button type="button" disabled={sibuk === e.id} onClick={() => ambilAlih(e)}
                          className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                          data-testid={`eskalasi-ambil-${e.id}`}>Saya ambil alih</button>
                )}
                <button type="button" disabled={sibuk === e.id} onClick={() => selesai(e)}
                        className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
                        data-testid={`eskalasi-selesai-${e.id}`}>Selesai</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
